import { useRef, useState } from "react";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 3 * 1024 * 1024; // 3 MB por imagen ya comprimida
const TARGET_WIDTH = 1280; // px máx (se reescala)
const JPEG_QUALITY = 0.78;

/**
 * Reescala y comprime una imagen a JPEG dataURL.
 * Devuelve { data_url, width, height, original_size, final_size }
 */
async function compressImage(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const scale = Math.min(1, TARGET_WIDTH / img.width);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                const data_url = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
                resolve({
                    data_url,
                    width: w,
                    height: h,
                    original_size: file.size,
                    final_size: data_url.length,
                });
            };
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * EvidenceUploader - permite subir múltiples fotos como evidencia.
 * Comprime client-side a JPEG ≤ ~3MB cada una y devuelve un array.
 *
 * value: [{ data_url, caption, timestamp, width, height }]
 * onChange: (next) => void
 */
export default function EvidenceUploader({ value = [], onChange, testId = "evidence-uploader" }) {
    const fileInput = useRef(null);
    const cameraInput = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleFiles = async (fileList) => {
        if (!fileList?.length) return;
        setBusy(true);
        try {
            const next = [...value];
            for (const file of Array.from(fileList)) {
                if (!file.type.startsWith("image/")) continue;
                const result = await compressImage(file);
                if (result.final_size > MAX_BYTES) {
                    toast.warning(`Imagen ${file.name} muy grande, se omitió`);
                    continue;
                }
                next.push({
                    data_url: result.data_url,
                    caption: "",
                    timestamp: new Date().toISOString(),
                    width: result.width,
                    height: result.height,
                    type: "photo",
                });
            }
            onChange?.(next);
            toast.success(`${fileList.length} evidencia(s) agregada(s)`);
        } catch (e) {
            toast.error("Error al procesar imagen");
            console.error(e);
        } finally {
            setBusy(false);
            if (fileInput.current) fileInput.current.value = "";
            if (cameraInput.current) cameraInput.current.value = "";
        }
    };

    const updateCaption = (i, caption) => {
        const next = value.map((ev, idx) => idx === i ? { ...ev, caption } : ev);
        onChange?.(next);
    };

    const remove = (i) => {
        const next = value.filter((_, idx) => idx !== i);
        onChange?.(next);
    };

    return (
        <div data-testid={testId} className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => cameraInput.current?.click()}
                    disabled={busy}
                    data-testid="evidence-camera-btn"
                    className="fm-btn-outline"
                >
                    <Camera className="w-3.5 h-3.5" /> Tomar foto
                </button>
                <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    disabled={busy}
                    data-testid="evidence-upload-btn"
                    className="fm-btn-outline"
                >
                    <Upload className="w-3.5 h-3.5" /> Subir imagen
                </button>
                <span className="text-[10px] text-slate-400 font-mono">
                    {value.length} archivo(s) · JPEG comprimido
                </span>
            </div>
            <input
                ref={cameraInput}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="evidence-camera-input"
            />
            <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="evidence-file-input"
            />

            {value.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-sm py-8 px-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                    <ImageIcon className="w-4 h-4" /> Sin evidencias adjuntas
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {value.map((ev, i) => (
                        <div key={i} className="border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid={`evidence-item-${i}`}>
                            <div className="relative">
                                <img src={ev.data_url} alt={ev.caption || `Evidencia ${i + 1}`} className="w-full h-28 object-cover" />
                                <button
                                    type="button"
                                    onClick={() => remove(i)}
                                    className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-700 text-white rounded-sm p-0.5"
                                    data-testid={`evidence-remove-${i}`}
                                    aria-label="Eliminar evidencia"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                            <input
                                value={ev.caption || ""}
                                onChange={(e) => updateCaption(i, e.target.value)}
                                placeholder="Descripción..."
                                className="w-full text-[11px] px-2 py-1 border-t border-slate-200 focus:outline-none focus:bg-slate-50"
                                data-testid={`evidence-caption-${i}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
